import type { ExerciseTemplate, Trap } from '@yuan-math/exercises';
import { roundTo } from '@yuan-math/math';
import {
  choiceExercise,
  decimalTolerance,
  numericExercise,
  options,
  template,
  trap,
} from './authoring.ts';

/**
 * The binomial, geometric, Poisson and normal distributions.
 *
 * A named distribution is a set of *conditions*, not a formula, so a third of
 * the items here ask which distribution applies rather than asking for a
 * number. The three discrete families are deliberately confusable in exactly
 * the ways students confuse them — binomial and geometric share their trials
 * and differ in what is counted; Poisson and binomial share their counting and
 * differ over whether there is an n at all — and every "which one" item offers
 * the other two as named distractors.
 *
 * Three parameter guards are load-bearing rather than tidy:
 *
 * `binomial-exact` keeps k off both ends *and* near np. At either end the
 * binomial coefficient is genuinely 1, so a student who omits coefficients
 * altogether grades as correct; far out in the tail the answer itself rounds to
 * 0.000 at the three decimal places the question asks for, and an item whose
 * answer is 0.000 accepts every trap within a thousandth of it — which is all
 * of them.
 *
 * `geometric-exact` refuses k = 1 for the same reason: the first success on the
 * first trial needs no failures, so the off-by-one exponent error produces the
 * right answer.
 *
 * `normal-tail-area` keeps every z off the whole numbers where the empirical
 * rule already answers the question. The concept exists because 1.37 is as
 * answerable as 2, and an item that only ever asks about 2 tests the Grade 11
 * lesson instead of this one.
 */

/** Drops any trap repeating the answer or an earlier trap. */
const distinct = (answer: number | string, traps: Trap[]): Trap[] => {
  const seen = new Set<string>([String(answer)]);
  return traps.filter((entry) => {
    if (seen.has(entry.response)) return false;
    seen.add(entry.response);
    return true;
  });
};

/** n choose k, without a factorial big enough to lose precision. */
function choose(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  let result = 1;
  for (let i = 0; i < k; i += 1) result = (result * (n - i)) / (i + 1);
  return Math.round(result);
}

const factorial = (k: number): number => {
  let result = 1;
  for (let i = 2; i <= k; i += 1) result *= i;
  return result;
};

const binomialPmf = (n: number, k: number, p: number): number =>
  choose(n, k) * p ** k * (1 - p) ** (n - k);

const poissonPmf = (lambda: number, k: number): number =>
  (Math.exp(-lambda) * lambda ** k) / factorial(k);

/**
 * Φ(z), the area to the left under the standard normal.
 *
 * Abramowitz & Stegun 7.1.26, which is accurate to about 1.5 × 10⁻⁷ — far
 * inside the three decimal places every item here asks for, and the reason
 * these templates need no table baked into the repository.
 */
function phi(z: number): number {
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * x);
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) *
      t *
      Math.exp(-x * x);
  return 0.5 * (1 + sign * y);
}

export const DISTRIBUTION_TEMPLATES: ExerciseTemplate[] = [
  /* ------------------------------------------------------------- binomial */
  template({
    id: 'binomial-exact',
    conceptId: 'binomial-distribution',
    description: 'An exact binomial probability, with the coefficient in play.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const n = rng.int(6, difficulty === 4 ? 16 : 12);
      const p = rng.pick([10, 15, 20, 25, 30, 40]) / 100;
      /*
       * k sits near np, and never at either end.
       *
       * Off the ends because there the binomial coefficient is genuinely 1, so
       * a student who omits coefficients altogether grades as correct. Near np
       * because a probability out in the tail rounds to 0.000 at the three
       * decimal places the question asks for — and an item whose *answer* is
       * 0.000 accepts every trap within a thousandth of it, which is all of
       * them.
       */
      const k = Math.min(n - 2, Math.max(2, Math.round(n * p) + rng.int(-1, 1)));
      const answer = roundTo(binomialPmf(n, k, p), 3);
      const swapped = roundTo(binomialPmf(n, k, 1 - p), 3);

      return numericExercise({
        prompt: `A component is faulty with probability ${p}, independently. In a box of ${n}, what is the probability that exactly ${k} are faulty? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `Arrangements, successes, failures: ${choose(n, k)} × ${p}^${k} × ${roundTo(1 - p, 2)}^${n - k} ≈ ${answer}.`,
        hint: 'How many different sets of components could be the faulty ones? Every one of those arrangements has the same probability.',
        steps: [
          { label: 'Count the arrangements.', latex: `{}^{${n}}C_{${k}} = ${choose(n, k)}` },
          { label: 'The successes and the failures.', latex: `${p}^{${k}} \\cdot ${roundTo(1 - p, 2)}^{${n - k}}` },
          { label: 'Multiply.', latex: `${answer}` },
        ],
        traps: distinct(answer, [
          // One specific sequence: the coefficient omitted.
          trap(roundTo(p ** k * (1 - p) ** (n - k), 3), 'binomial-coefficients-omitted'),
          // Every trial required to fail, not just the n − k that must.
          trap(roundTo(choose(n, k) * p ** k * (1 - p) ** n, 3), 'binomial-failure-exponent-wrong'),
          /*
           * p and 1 − p interchanged — but only when that lands somewhere a
           * student would actually write. Out in the tail it rounds to 0.000,
           * which is also where the coefficient-omitted answer lands, and one
           * response cannot carry two diagnoses.
           */
          ...(swapped >= 0.0005 ? [trap(swapped, 'binomial-trials-and-successes-swapped')] : []),
        ]),
      });
    },
  }),

  template({
    id: 'binomial-cumulative',
    conceptId: 'binomial-distribution',
    description: 'A cumulative binomial probability, by complement.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const n = rng.int(8, 16);
      const p = rng.pick([10, 15, 20, 25, 30]) / 100;
      const cut = rng.int(1, 3);
      let atMost = 0;
      for (let i = 0; i <= cut; i += 1) atMost += binomialPmf(n, i, p);
      const answer = roundTo(1 - atMost, 3);

      return numericExercise({
        prompt: `In ${n} independent trials each succeeding with probability ${p}, what is the probability of more than ${cut} success${cut === 1 ? '' : 'es'}? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `"More than ${cut}" means ${cut + 1} or more, which is ${n - cut} separate terms. Its complement is "at most ${cut}", which is ${cut + 1}: P(X ≤ ${cut}) ≈ ${roundTo(atMost, 4)}, so the answer is 1 − that ≈ ${answer}.`,
        hint: 'Count how many terms each side of the complement needs, then compute the shorter one.',
        steps: [
          { label: 'Identify the shorter sum.', latex: `P(X \\le ${cut}) = \\sum_{i=0}^{${cut}} {}^{${n}}C_{i}\\,p^{i}(1-p)^{${n}-i}` },
          { label: 'Evaluate it.', latex: `\\approx ${roundTo(atMost, 4)}` },
          { label: 'Take the complement.', latex: `1 - ${roundTo(atMost, 4)} \\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // The complement reported instead of subtracted.
          trap(roundTo(atMost, 3), 'complement-not-subtracted'),
          // The boundary value included, giving P(X ≥ cut) instead.
          trap(roundTo(1 - atMost + binomialPmf(n, cut, p), 3), 'cumulative-boundary-excluded'),
        ]),
      });
    },
  }),

  template({
    id: 'binomial-mean-and-sd',
    conceptId: 'binomial-distribution',
    description: 'Mean and standard deviation of a binomial, without summing.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const n = rng.pick([20, 25, 40, 50, 60, 80, 100]);
      const p = rng.pick([10, 20, 25, 40, 50]) / 100;
      const variance = n * p * (1 - p);
      const answer = roundTo(Math.sqrt(variance), 3);

      return numericExercise({
        prompt: `X is binomial with ${n} trials and success probability ${p}. What is the standard deviation of X? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `Var(X) = np(1 − p) = ${n} × ${p} × ${roundTo(1 - p, 2)} = ${roundTo(variance, 3)}, and σ is its square root: ${answer}.`,
        hint: 'Two formulas, and only one of them needs a square root taken at the end.',
        steps: [
          { label: 'The mean, for orientation.', latex: `np = ${roundTo(n * p, 2)}` },
          { label: 'The variance.', latex: `np(1-p) = ${roundTo(variance, 3)}` },
          { label: 'The standard deviation.', latex: `\\sqrt{${roundTo(variance, 3)}} \\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // The variance, never square-rooted.
          trap(roundTo(variance, 3), 'variance-not-rooted'),
          // The mean, offered where the spread was asked for.
          trap(roundTo(n * p, 3), 'spread-judged-by-centre'),
          // √(np), with the failure factor dropped.
          trap(roundTo(Math.sqrt(n * p), 3), 'binomial-failure-exponent-wrong'),
        ]),
      });
    },
  }),

  template({
    id: 'binomial-conditions-choice',
    conceptId: 'binomial-distribution',
    description: 'Decide whether a situation satisfies the four conditions.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const n = rng.int(4, 8);
      const bag = rng.int(15, 40);
      const scenario = rng.pick([
        {
          text: `${n} cards are dealt from a shuffled deck and the number of hearts is counted`,
          binomial: false,
          why: 'the cards are not replaced, so p changes and the draws are not independent',
          fault: 'binomial-conditions-not-checked',
        },
        {
          text: `a coin is tossed until the first head appears, and the number of tosses is counted`,
          binomial: false,
          why: 'the number of trials is not fixed, since that is what is being counted',
          fault: 'binomial-conditions-not-checked',
        },
        {
          text: `${n} independent components are tested and the number that fail is counted`,
          binomial: true,
          why: 'fixed n, two outcomes, a constant failure probability and independent trials',
          fault: 'binomial-conditions-not-checked',
        },
        {
          text: `${n} counters are drawn *with replacement* from a bag of ${bag} and the number of red ones is counted`,
          binomial: true,
          why: 'replacement restores the bag, so p is constant and the draws are independent',
          fault: 'binomial-conditions-not-checked',
        },
      ]);

      const opened = `${scenario.text.charAt(0).toUpperCase()}${scenario.text.slice(1)}`;

      return choiceExercise({
        prompt: `Is this binomial? ${opened}.`,
        ...options(
          [
            scenario.binomial
              ? { label: 'Yes — all four conditions hold', correct: true }
              : { label: `No — ${scenario.why}`, correct: true },
            scenario.binomial
              ? { label: 'No — the trials cannot really be independent', misconceptionId: scenario.fault }
              : { label: 'Yes — each trial is a success or a failure, which is enough', misconceptionId: scenario.fault },
            { label: 'It is geometric rather than binomial', misconceptionId: 'geometric-coefficient-included' },
            { label: 'It is Poisson rather than binomial', misconceptionId: 'poisson-lambda-not-scaled' },
          ],
          rng.shuffle,
        ),
        summary: `${scenario.binomial ? 'Binomial' : 'Not binomial'}: ${scenario.why}. Check fixed n, two outcomes, constant p and independence, in that order.`,
        hint: 'Go through the four conditions one at a time. Which of them, if any, does this situation break?',
      });
    },
  }),

  /* ------------------------------------------------------------ geometric */
  template({
    id: 'geometric-exact',
    conceptId: 'geometric-distribution',
    description: 'The first success on a given trial.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const p = rng.pick([5, 8, 10, 15, 20, 25, 30, 40]) / 100;
      /* k = 1 needs no failures, and there the off-by-one exponent is right. */
      const k = rng.int(2, difficulty === 3 ? 8 : 5);
      const answer = roundTo((1 - p) ** (k - 1) * p, 4);

      return numericExercise({
        prompt: `A machine jams on any cycle with probability ${p}, independently. What is the probability that the first jam is on cycle ${k}? Give your answer to three decimal places.`,
        value: roundTo(answer, 3),
        tolerance: decimalTolerance(roundTo(answer, 3), 3),
        summary: `The first ${k - 1} cycles must run clean and the ${k}th must jam: ${roundTo(1 - p, 2)}^${k - 1} × ${p} ≈ ${roundTo(answer, 3)}.`,
        hint: 'How many cycles must go the other way before the one you are waiting for?',
        steps: [
          { label: 'The clean cycles.', latex: `${roundTo(1 - p, 2)}^{${k - 1}}` },
          { label: 'Then the jam.', latex: `\\times\\, ${p}` },
          { label: 'Multiply.', latex: `\\approx ${roundTo(answer, 3)}` },
        ],
        traps: distinct(roundTo(answer, 3), [
          // k failures counted rather than k − 1.
          trap(roundTo((1 - p) ** k * p, 3), 'geometric-exponent-off-by-one'),
          // A coefficient added, as though the successes could be arranged.
          trap(roundTo(k * (1 - p) ** (k - 1) * p, 3), 'geometric-coefficient-included'),
        ]),
      });
    },
  }),

  template({
    id: 'geometric-tail',
    conceptId: 'geometric-distribution',
    description: 'The probability of waiting longer than k trials.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const p = rng.pick([5, 8, 10, 12, 15, 20, 25]) / 100;
      const k = rng.int(4, 12);
      const answer = roundTo((1 - p) ** k, 3);

      return numericExercise({
        prompt: `A test succeeds with probability ${p} on each independent attempt. What is the probability that the first success takes more than ${k} attempts? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `Needing more than ${k} attempts means the first ${k} all failed: ${roundTo(1 - p, 2)}^${k} ≈ ${answer}.`,
        hint: 'What has to have happened on every one of the first k attempts?',
        steps: [
          { label: 'Every one of the first attempts fails.', latex: `(1 - ${p})^{${k}}` },
          { label: 'Evaluate.', latex: `\\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // The complement: succeeding within k attempts.
          trap(roundTo(1 - (1 - p) ** k, 3), 'complement-not-subtracted'),
          // The exponent off by one — the boundary attempt double-counted.
          trap(roundTo((1 - p) ** (k - 1), 3), 'geometric-exponent-off-by-one'),
        ]),
      });
    },
  }),

  template({
    id: 'geometric-expected-wait',
    conceptId: 'geometric-distribution',
    description: 'The expected number of trials until the first success.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const p = rng.pick([2, 4, 5, 8, 10, 16, 20, 25, 40, 50]) / 100;
      const answer = roundTo(1 / p, 2);

      return numericExercise({
        prompt: `Each attempt succeeds with probability ${p}, independently. On average, how many attempts are needed to get the first success? Give your answer to two decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `E(X) = 1/p = 1 ÷ ${p} = ${answer} attempts. A 1-in-n event takes about n tries on average.`,
        hint: 'If something works one time in five, how many tries would you expect it to take?',
        steps: [
          { label: 'The geometric mean.', latex: `E(X) = \\frac{1}{p}` },
          { label: 'Substitute.', latex: `\\frac{1}{${p}} = ${answer}` },
        ],
        traps: distinct(answer, [
          // p itself, quoted where a number of trials was asked for.
          trap(p, 'geometric-mean-inverted'),
          // The failure probability.
          trap(roundTo(1 - p, 2), 'geometric-mean-inverted'),
          // The most likely value, which is always 1.
          trap(1, 'geometric-mean-inverted'),
        ]),
      });
    },
  }),

  template({
    id: 'binomial-or-geometric',
    conceptId: 'geometric-distribution',
    description: 'Choose between counting successes and counting trials.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const n = rng.int(8, 20);
      const p = rng.pick([10, 20, 25, 30, 40]) / 100;
      const geometric = rng.bool();
      const story = geometric
        ? `A salesperson calls prospects one after another, each buying with probability ${p}. Let X be the number of calls up to and including the first sale.`
        : `A salesperson makes ${n} calls, each resulting in a sale with probability ${p}. Let X be the number of sales.`;

      return choiceExercise({
        prompt: `${story} Which distribution does X have?`,
        ...options(
          [
            geometric
              ? { label: `Geometric with p = ${p} — the number of trials is not fixed`, correct: true }
              : { label: `Binomial with n = ${n} and p = ${p} — a fixed number of independent trials`, correct: true },
            geometric
              ? { label: `Binomial with n = ${n} and p = ${p}`, misconceptionId: 'binomial-conditions-not-checked' }
              : { label: `Geometric with p = ${p}`, misconceptionId: 'geometric-coefficient-included' },
            { label: `Poisson with a rate of ${roundTo(n * p, 1)}`, misconceptionId: 'poisson-lambda-not-scaled' },
            { label: 'None of these — the trials are not independent', misconceptionId: 'binomial-conditions-not-checked' },
          ],
          rng.shuffle,
        ),
        summary: geometric
          ? 'Counting trials until the first success, with no upper limit on how many there might be, is geometric. Nothing fixes n in advance.'
          : `Counting successes out of a fixed ${n} independent trials with constant p is binomial. X can be anything from 0 to ${n}.`,
        hint: 'Ask what the question counts: successes out of a fixed number of tries, or tries until a success?',
      });
    },
  }),

  /* -------------------------------------------------------------- Poisson */
  template({
    id: 'poisson-rescaled-interval',
    conceptId: 'poisson-distribution',
    description: 'Scale the rate to the interval before substituting.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const rate = rng.pick([0.4, 0.5, 0.6, 0.8, 1.2, 1.5, 2]);
      const stretch = rng.int(2, 6);
      const lambda = roundTo(rate * stretch, 2);
      const k = rng.int(0, 4);
      const answer = roundTo(poissonPmf(lambda, k), 3);

      return numericExercise({
        prompt: `Faults occur in a cable at an average rate of ${rate} per kilometre, independently and at a constant rate. What is the probability of exactly ${k} fault${k === 1 ? '' : 's'} in ${stretch} km? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `Scale the rate first: λ = ${rate} × ${stretch} = ${lambda}. Then P(X = ${k}) = e^(−${lambda}) × ${lambda}^${k} ÷ ${k}! ≈ ${answer}.`,
        hint: 'The rate was quoted per kilometre and the question asks about several. What has to change before you substitute?',
        steps: [
          { label: 'Rescale the rate to the interval.', latex: `\\lambda = ${rate} \\times ${stretch} = ${lambda}` },
          { label: 'Substitute into the formula.', latex: `\\frac{e^{-${lambda}}\\,${lambda}^{${k}}}{${k}!}` },
          { label: 'Evaluate.', latex: `\\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // The rate used unscaled — the answer for one kilometre.
          trap(roundTo(poissonPmf(rate, k), 3), 'poisson-lambda-not-scaled'),
          // The factorial left out.
          ...(k > 1 ? [trap(roundTo(Math.exp(-lambda) * lambda ** k, 3), 'poisson-factorial-omitted')] : []),
        ]),
      });
    },
  }),

  template({
    id: 'poisson-at-least',
    conceptId: 'poisson-distribution',
    description: 'A cumulative Poisson probability, by complement.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const lambda = rng.pick([1, 1.5, 2, 2.5, 3, 4, 5]);
      const cut = rng.int(1, 3);
      let below = 0;
      for (let i = 0; i < cut; i += 1) below += poissonPmf(lambda, i);
      const answer = roundTo(1 - below, 3);

      return numericExercise({
        prompt: `Calls arrive at a switchboard at an average of ${lambda} per minute, independently and at a constant rate. What is the probability of at least ${cut} call${cut === 1 ? '' : 's'} in a minute? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `"At least ${cut}" is an infinite sum; its complement, "fewer than ${cut}", is ${cut} term${cut === 1 ? '' : 's'}. P(X < ${cut}) ≈ ${roundTo(below, 4)}, so the answer is 1 − that ≈ ${answer}.`,
        hint: 'A Poisson variable has no largest value, so summing upwards never ends. Which direction is finite?',
        steps: [
          { label: 'Sum the terms below the cut.', latex: `P(X < ${cut}) \\approx ${roundTo(below, 4)}` },
          { label: 'Take the complement.', latex: `1 - ${roundTo(below, 4)} \\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // The complement reported rather than subtracted.
          trap(roundTo(below, 3), 'complement-not-subtracted'),
          // The boundary value excluded, giving "more than cut".
          trap(roundTo(1 - below - poissonPmf(lambda, cut), 3), 'cumulative-boundary-excluded'),
        ]),
      });
    },
  }),

  template({
    id: 'poisson-mean-equals-variance',
    conceptId: 'poisson-distribution',
    description: 'Recognise the distribution whose mean and variance coincide.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const lambda = rng.pick([1.5, 2, 2.5, 3, 4, 5, 6, 7.5, 9]);
      const answer = roundTo(Math.sqrt(lambda), 3);

      return numericExercise({
        prompt: `X is Poisson with mean ${lambda}. What is the standard deviation of X? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `A Poisson distribution has Var(X) = λ, the same number as its mean — no other distribution on this course does that. So σ = √${lambda} ≈ ${answer}.`,
        hint: 'What is the variance of a Poisson variable? It is a number you have already been given.',
        steps: [
          { label: 'For a Poisson variable the variance equals the mean.', latex: `\\operatorname{Var}(X) = \\lambda = ${lambda}` },
          { label: 'Take the square root.', latex: `\\sqrt{${lambda}} \\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // The variance, never square-rooted.
          trap(lambda, 'variance-not-rooted'),
          // λ² — the mean squared, as though it were the variance.
          trap(roundTo(lambda * lambda, 3), 'variance-not-rooted'),
        ]),
      });
    },
  }),

  template({
    id: 'which-distribution',
    conceptId: 'poisson-distribution',
    description: 'Pick the distribution a situation actually satisfies.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const n = rng.int(10, 30);
      const rate = rng.pick([2, 3, 4, 5, 6]);
      const p = rng.pick([10, 20, 25, 30]) / 100;
      const scenario = rng.pick([
        {
          text: `typing errors appear on a page at an average of ${rate} per page, with no fixed number of opportunities`,
          answer: `Poisson with λ = ${rate}`,
          wrong: [
            { label: `Binomial with n = ${n} and p = ${p}`, misconceptionId: 'binomial-conditions-not-checked' },
            { label: `Geometric with p = ${p}`, misconceptionId: 'geometric-coefficient-included' },
          ],
          why: 'there is no n to count out of — "how many errors out of how many chances?" has no answer, which is exactly what Poisson is for',
        },
        {
          text: `${n} independent items are inspected and the number of defectives is counted, each defective with probability ${p}`,
          answer: `Binomial with n = ${n} and p = ${p}`,
          wrong: [
            { label: `Poisson with λ = ${rate}`, misconceptionId: 'poisson-lambda-not-scaled' },
            { label: `Geometric with p = ${p}`, misconceptionId: 'geometric-coefficient-included' },
          ],
          why: `n is fixed at ${n} and each trial has the same probability, which is the binomial's whole specification`,
        },
        {
          text: `accidents occur at a junction at an average of ${rate} per month, independently and at a steady rate`,
          answer: `Poisson with λ = ${rate}`,
          wrong: [
            { label: `Binomial with n = 30 and p = ${p}`, misconceptionId: 'binomial-conditions-not-checked' },
            { label: `Geometric with p = ${p}`, misconceptionId: 'geometric-coefficient-included' },
          ],
          why: 'an accident can happen at any instant, so there is no list of trials to count successes out of',
        },
      ]);

      return choiceExercise({
        prompt: `Which distribution models this? ${scenario.text.charAt(0).toUpperCase()}${scenario.text.slice(1)}.`,
        ...options(
          [
            { label: scenario.answer, correct: true },
            ...scenario.wrong,
            { label: 'Normal, because counts are always bell-shaped', misconceptionId: 'normal-assumed-for-any-data' },
          ],
          rng.shuffle,
        ),
        summary: `${scenario.answer}: ${scenario.why}.`,
        hint: 'Ask first whether there is a fixed number of trials. If "out of how many?" has no answer, the count is Poisson.',
      });
    },
  }),

  /* --------------------------------------------------------------- normal */
  template({
    id: 'normal-tail-area',
    conceptId: 'normal-random-variable',
    description: 'An area under the bell curve at a z the empirical rule cannot reach.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const mean = rng.pick([50, 100, 170, 200, 500]);
      const sd = rng.pick([4, 5, 8, 10, 12, 15, 20]);
      /*
       * z is deliberately kept off the whole numbers. This concept exists
       * because 1.37 is as answerable as 2, and an item that only ever lands on
       * 1, 2 or 3 tests the Grade 11 empirical rule instead of this lesson.
       */
      const zTimes100 = rng.pick([37, 63, 84, 112, 137, 158, 171, 224, 246]);
      const above = rng.bool();
      const z = zTimes100 / 100;
      const value = roundTo(mean + z * sd, 2);
      const left = phi(z);
      const answer = roundTo(above ? 1 - left : left, 3);

      return numericExercise({
        prompt: `A measurement is normally distributed with mean ${mean} and standard deviation ${sd}. What proportion of values are ${above ? 'above' : 'below'} ${value}? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        figure: {
          kind: 'normal-curve',
          mean,
          sd,
          shade: above ? { from: z, to: 3.4 } : { from: -3.4, to: z },
          marks: [-2, -1, 0, 1, 2],
          label: `z = ${z}`,
        },
        summary: `Standardise: z = (${value} − ${mean}) ÷ ${sd} = ${z}. The table gives the area to the left, Φ(${z}) ≈ ${roundTo(left, 4)}${above ? `, so the area above is 1 − that ≈ ${answer}` : ` ≈ ${answer}`}.`,
        hint: 'Standardise first, then decide whether you want the tabulated area or what is left of the curve.',
        steps: [
          { label: 'Standardise.', latex: `z = \\frac{${value} - ${mean}}{${sd}} = ${z}` },
          { label: 'Read the area to the left.', latex: `\\Phi(${z}) \\approx ${roundTo(left, 4)}` },
          {
            label: above ? 'The question wants the other side.' : 'That is the side asked for.',
            latex: `${answer}`,
          },
        ],
        traps: distinct(answer, [
          // The other tail — the table's area used without complementing.
          trap(roundTo(above ? left : 1 - left, 3), 'normal-area-complemented'),
          // The distance from the mean given as the z-score.
          trap(roundTo(value - mean, 3), 'z-score-not-divided-by-sd'),
        ]),
      });
    },
  }),

  template({
    id: 'inverse-normal-cutoff',
    conceptId: 'normal-random-variable',
    description: 'Run the calculation backwards, from a proportion to a value.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const mean = rng.pick([50, 100, 150, 200, 500]);
      const sd = rng.pick([4, 5, 8, 10, 12, 20]);
      const entry = rng.pick([
        { percent: 5, z: 1.645 },
        { percent: 10, z: 1.282 },
        { percent: 15, z: 1.036 },
        { percent: 20, z: 0.842 },
        { percent: 25, z: 0.674 },
        { percent: 2.5, z: 1.96 },
        { percent: 1, z: 2.326 },
      ]);
      const top = rng.bool();
      const z = top ? entry.z : -entry.z;
      const answer = roundTo(mean + z * sd, 2);

      return numericExercise({
        prompt: `A measurement is normally distributed with mean ${mean} and standard deviation ${sd}. ${top ? 'Above' : 'Below'} what value do the ${top ? 'highest' : 'lowest'} ${entry.percent}% lie? Give your answer to two decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `An area of ${top ? roundTo(1 - entry.percent / 100, 4) : entry.percent / 100} to the left corresponds to z = ${z}. Un-standardise: x = ${mean} + (${z})(${sd}) = ${answer}.`,
        hint: 'The table is indexed by the area to the LEFT. What area to the left does this question describe?',
        steps: [
          { label: 'Convert the question to an area on the left.', latex: `${top ? roundTo(1 - entry.percent / 100, 4) : entry.percent / 100}` },
          { label: 'Find the z with that area.', latex: `z = ${z}` },
          { label: 'Un-standardise.', latex: `x = ${mean} + (${z})(${sd}) = ${answer}` },
        ],
        traps: distinct(answer, [
          // The z applied on the wrong side of the mean.
          trap(roundTo(mean - z * sd, 2), 'inverse-normal-tail-swapped'),
          // The z-score reported instead of the value it points to.
          trap(z, 'z-score-not-divided-by-sd'),
          // The mean, unmoved.
          trap(mean, 'inverse-normal-tail-swapped'),
        ]),
      });
    },
  }),

  template({
    id: 'normal-approximation-to-binomial',
    conceptId: 'normal-random-variable',
    description: 'Approximate a binomial, with the continuity correction.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const n = rng.pick([50, 80, 100, 120, 150, 200]);
      const p = rng.pick([20, 25, 30, 40, 50]) / 100;
      const mean = n * p;
      const sd = Math.sqrt(n * p * (1 - p));
      const cut = Math.round(mean + rng.pick([1.2, 1.5, 1.8, 2.1]) * sd);
      const zCorrected = (cut - 0.5 - mean) / sd;
      const answer = roundTo(1 - phi(zCorrected), 3);
      const zUncorrected = (cut - mean) / sd;

      return numericExercise({
        prompt: `X is binomial with ${n} trials and success probability ${p}. Using a normal approximation with a continuity correction, estimate P(X ≥ ${cut}). Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        summary: `np = ${roundTo(mean, 2)} and σ = √(np(1 − p)) ≈ ${roundTo(sd, 3)}. "At least ${cut}" starts at the left edge of the ${cut} bar, which is ${cut - 0.5}: z = ${roundTo(zCorrected, 3)}, so the upper tail is ≈ ${answer}.`,
        hint: 'The bar for X = ' + cut + ' runs from ' + (cut - 0.5) + ' to ' + (cut + 0.5) + '. Which of its edges does "at least" begin at?',
        steps: [
          { label: 'Mean and standard deviation of the binomial.', latex: `\\mu = ${roundTo(mean, 2)}, \\quad \\sigma \\approx ${roundTo(sd, 3)}` },
          { label: 'Apply the continuity correction.', latex: `${cut} - 0.5 = ${cut - 0.5}` },
          { label: 'Standardise.', latex: `z = \\frac{${cut - 0.5} - ${roundTo(mean, 2)}}{${roundTo(sd, 3)}} \\approx ${roundTo(zCorrected, 3)}` },
          { label: 'Read the upper tail.', latex: `1 - \\Phi(${roundTo(zCorrected, 3)}) \\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // The correction omitted — half a bar thrown away.
          trap(roundTo(1 - phi(zUncorrected), 3), 'continuity-correction-omitted'),
          // The correction applied in the wrong direction.
          trap(roundTo(1 - phi((cut + 0.5 - mean) / sd), 3), 'continuity-correction-omitted'),
          // The lower tail reported.
          trap(roundTo(phi(zCorrected), 3), 'normal-area-complemented'),
        ]),
      });
    },
  }),

  template({
    id: 'normal-between-two-values',
    conceptId: 'normal-random-variable',
    description: 'The area between two values, as a difference of two lookups.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const mean = rng.pick([50, 100, 170, 250, 500]);
      const sd = rng.pick([4, 5, 8, 10, 15, 20]);
      const zLow = -rng.pick([0.42, 0.73, 1.15, 1.28, 1.64]);
      const zHigh = rng.pick([0.58, 0.96, 1.34, 1.72, 2.05]);
      const low = roundTo(mean + zLow * sd, 2);
      const high = roundTo(mean + zHigh * sd, 2);
      const answer = roundTo(phi(zHigh) - phi(zLow), 3);

      return numericExercise({
        prompt: `A measurement is normally distributed with mean ${mean} and standard deviation ${sd}. What proportion of values lie between ${low} and ${high}? Give your answer to three decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 3),
        figure: {
          kind: 'normal-curve',
          mean,
          sd,
          shade: { from: zLow, to: zHigh },
          marks: [-2, -1, 0, 1, 2],
          label: `z from ${zLow} to ${zHigh}`,
        },
        summary: `Standardise both ends: z = ${zLow} and z = ${zHigh}. Then subtract the areas to the left, larger first: ${roundTo(phi(zHigh), 4)} − ${roundTo(phi(zLow), 4)} ≈ ${answer}.`,
        hint: 'Everything left of the upper value, minus everything left of the lower one. What is left over is the band between them.',
        steps: [
          { label: 'Standardise both ends.', latex: `z_1 = ${zLow}, \\quad z_2 = ${zHigh}` },
          { label: 'Look both areas up.', latex: `\\Phi(${zHigh}) \\approx ${roundTo(phi(zHigh), 4)}, \\quad \\Phi(${zLow}) \\approx ${roundTo(phi(zLow), 4)}` },
          { label: 'Subtract, larger first.', latex: `\\approx ${answer}` },
        ],
        traps: distinct(answer, [
          // The complement: the two tails rather than the band.
          trap(roundTo(1 - (phi(zHigh) - phi(zLow)), 3), 'normal-area-complemented'),
          // Only the upper lookup, with the lower end never subtracted.
          trap(roundTo(phi(zHigh), 3), 'normal-area-complemented'),
        ]),
      });
    },
  }),
];
